import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, KeyRound, RefreshCw } from 'lucide-react';
import type { ApiKeyToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Pill } from '~/components/pill';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { formatDateTime } from '~/lib/format-date';
import { useApiKeysList, useApiKeyEndpoints, useApiKeyMutations } from '~/hooks/use-api-keys';
import { type ApiKeyFormValues } from '~/lib/schemas/api-key';
import { ApiKeyEditDialog } from './api-key-edit-dialog';
import { MintedSecretDialog } from './minted-secret-dialog';

function tokenToForm(k: ApiKeyToken): ApiKeyFormValues {
	return { id: k.id, name: k.name ?? '', description: k.description ?? null, endpointIds: k.endpointIds ?? [] };
}

/** API keys surface — create (one-time secret), edit/scope, regenerate, revoke, delete. */
export function ApiKeysGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useApiKeysList();
	const { data: endpoints = [] } = useApiKeyEndpoints();
	const { regenerate, revoke, remove } = useApiKeyMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<ApiKeyFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<ApiKeyToken | null>(null);
	const [revokeRow, setRevokeRow] = useState<ApiKeyToken | null>(null);
	const [minted, setMinted] = useState<{ token: string; name: string } | null>(null);

	const columns = useMemo<SystemGridColumn<ApiKeyToken>[]>(
		() => [
			{
				key: 'name',
				header: t('system.apiKeys.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: k => k.name ?? '',
				exportValue: k => k.name ?? '',
				render: k => (
					<div className="min-w-0">
						<div className="truncate text-sm font-medium text-foreground">{k.name}</div>
						{k.description && <div className="truncate text-[11px] text-foreground-muted">{k.description}</div>}
					</div>
				)
			},
			{
				key: 'key',
				header: t('system.apiKeys.columns.key'),
				width: '120px',
				exportValue: k => `••••${k.keyLast4 ?? ''}`,
				render: k => <span className="font-mono text-xs text-foreground-muted">••••{k.keyLast4 || '????'}</span>
			},
			{
				key: 'endpoints',
				header: t('system.apiKeys.columns.endpoints'),
				width: '110px',
				align: 'center',
				sortValue: k => k.endpointIds?.length ?? 0,
				exportValue: k => k.endpointIds?.length ?? 0,
				render: k => (
					<span className="rounded-pill bg-muted px-2 py-0.5 text-xs font-medium text-foreground-muted">
						{t('system.apiKeys.endpointCount', { count: k.endpointIds?.length ?? 0 })}
					</span>
				)
			},
			{
				key: 'lastUsed',
				header: t('system.apiKeys.columns.lastUsed'),
				width: '170px',
				sortValue: k => (k.lastUsedAt ? String(k.lastUsedAt) : ''),
				exportValue: k => formatDateTime(k.lastUsedAt),
				render: k => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(k.lastUsedAt)}</span>
			},
			{
				key: 'status',
				header: t('system.apiKeys.columns.status'),
				width: '110px',
				align: 'center',
				sortValue: k => (k.revokedAt ? 1 : 0),
				exportValue: k => (k.revokedAt ? t('system.apiKeys.revoked') : t('system.apiKeys.active')),
				render: k =>
					k.revokedAt ? (
						<Pill variant="danger">{t('system.apiKeys.revoked')}</Pill>
					) : (
						<Pill variant="success">{t('system.apiKeys.active')}</Pill>
					)
			},
			{
				key: 'description',
				header: t('system.fields.description'),
				width: '220px',
				defaultVisible: false,
				sortValue: k => k.description ?? '',
				exportValue: k => k.description ?? '',
				render: k => <span className="truncate text-sm text-foreground-muted">{k.description || '—'}</span>
			},
			{
				key: 'createdAt',
				header: t('system.fields.createdAt'),
				width: '170px',
				defaultVisible: false,
				sortValue: k => (k.createdAt ? String(k.createdAt) : ''),
				exportValue: k => formatDateTime(k.createdAt),
				render: k => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(k.createdAt)}</span>
			},
			{
				key: 'createdBy',
				header: t('system.fields.createdBy'),
				width: '200px',
				defaultVisible: false,
				sortValue: k => k.createdByEmail ?? '',
				exportValue: k => k.createdByEmail ?? '',
				render: k => <span className="truncate text-sm text-foreground-muted">{k.createdByEmail || '—'}</span>
			},
			{
				key: 'lastUsedIp',
				header: t('system.fields.lastUsedIp'),
				width: '140px',
				defaultVisible: false,
				sortValue: k => k.lastUsedIp ?? '',
				exportValue: k => k.lastUsedIp ?? '',
				render: k => <span className="font-mono text-xs text-foreground-muted">{k.lastUsedIp || '—'}</span>
			},
			{
				key: 'userAgent',
				header: t('system.fields.userAgent'),
				width: '220px',
				defaultVisible: false,
				sortValue: k => k.lastUsedUserAgent ?? '',
				exportValue: k => k.lastUsedUserAgent ?? '',
				render: k => (
					<span className="truncate text-xs text-foreground-muted" title={k.lastUsedUserAgent ?? ''}>
						{k.lastUsedUserAgent || '—'}
					</span>
				)
			},
			{
				key: 'revokedAt',
				header: t('system.fields.revokedAt'),
				width: '170px',
				defaultVisible: false,
				sortValue: k => (k.revokedAt ? String(k.revokedAt) : ''),
				exportValue: k => formatDateTime(k.revokedAt),
				render: k => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(k.revokedAt)}</span>
			}
		],
		[t]
	);

	const onRegenerate = (k: ApiKeyToken) => {
		if (!k.id) return;
		regenerate.mutate(k.id, {
			onSuccess: res => {
				if (res.token) setMinted({ token: res.token, name: k.name ?? '' });
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	const confirmRevoke = () => {
		if (!revokeRow?.id) return;
		revoke.mutate(revokeRow.id, {
			onSuccess: () => {
				toast.success(t('system.apiKeys.revokedToast'));
				setRevokeRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ApiKeyToken>
				gridId="api-keys"
				title={t('system.apiKeys.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={k => k.id ?? k.name ?? ''}
				columns={columns}
				searchPredicate={(k, q) => `${k.name ?? ''} ${k.description ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<KeyRound size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.apiKeys.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={k => {
					setEditing(tokenToForm(k));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				rowActions={k =>
					k.revokedAt ? null : (
						<>
							<Button
								variant="ghost"
								size="icon"
								type="button"
								aria-label={t('system.apiKeys.regenerate')}
								title={t('system.apiKeys.regenerate')}
								disabled={regenerate.isPending}
								onClick={() => onRegenerate(k)}>
								<RefreshCw size={15} />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								type="button"
								aria-label={t('system.apiKeys.revoke')}
								title={t('system.apiKeys.revoke')}
								onClick={() => setRevokeRow(k)}>
								<Ban size={15} className="text-danger-fg" />
							</Button>
						</>
					)
				}
				actionsWidth={148}
				exportFilename="api-keys"
			/>

			<ApiKeyEditDialog
				open={editOpen}
				onOpenChange={setEditOpen}
				initial={editing}
				endpoints={endpoints}
				onMinted={(token, name) => setMinted({ token, name })}
			/>

			<MintedSecretDialog token={minted?.token ?? null} keyName={minted?.name} onClose={() => setMinted(null)} />

			<Dialog open={Boolean(revokeRow)} onOpenChange={open => !open && setRevokeRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.apiKeys.revokeTitle')}</DialogTitle>
						<DialogDescription>{t('system.apiKeys.revokeBody', { name: revokeRow?.name ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setRevokeRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmRevoke} disabled={revoke.isPending}>
							{t('system.apiKeys.revoke')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.apiKeys.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.apiKeys.deleteBody', { name: deleteRow?.name ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
